"use client";

import { useTranslations } from "next-intl";

const ROW_KEYS = ["class105", "class104", "class103", "class102", "class101"] as const;

export interface InjectionMoldingCapabilitiesTableProps {
	/** 翻译命名空间，需包含 title、description、表头 colClass/colPurpose/colShotLife/colTolerance/colCost/colLeadTime，各行 class105～101 的 purpose/shotLife/tolerance/cost/leadTime */
	translationNamespace: string;
}

/**
 * Injection Molding Capabilities：暖白底、左标题+绿条、右描述、仅横线分隔的 6 列表格
 * 样式：背景 #f9f9f2，表头 Class | Purpose | Shot Life | Tolerance | Cost | Lead Time，无竖线
 */
export default function InjectionMoldingCapabilitiesTable({
	translationNamespace,
}: InjectionMoldingCapabilitiesTableProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-[#f9f9f2] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题（左）+ 描述（右），顶部对齐 */}
				<div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:items-start">
					<div>
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>
						<div className="mt-2 h-1 w-24 shrink-0 rounded bg-[#68a339] md:w-32" aria-hidden />
					</div>
					<div className="md:pt-0">
						<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
					</div>
				</div>

				{/* 仅横线分隔的 6 列表格 */}
				<div className="mt-10 overflow-x-auto md:mt-12">
					<table className="w-full min-w-[700px] border-collapse">
						<thead>
							<tr className="border-b border-[#e0e0e0]">
								<th className="pb-4 pt-0 text-left text-sm font-bold text-black md:pb-5 md:text-base">
									{t("colClass")}
								</th>
								<th className="pb-4 pt-0 text-left text-sm font-bold text-black md:pb-5 md:text-base">
									{t("colPurpose")}
								</th>
								<th className="pb-4 pt-0 text-left text-sm font-bold text-black md:pb-5 md:text-base lg:pl-[100px]">
									{t("colShotLife")}
								</th>
								<th className="pb-4 pt-0 text-left text-sm font-bold text-black md:pb-5 md:text-base">
									{t("colTolerance")}
								</th>
								<th className="pb-4 pt-0 text-left text-sm font-bold text-black md:pb-5 md:text-base">
									{t("colCost")}
								</th>
								<th className="pb-4 pt-0 text-left text-sm font-bold text-black md:pb-5 md:text-base">
									{t("colLeadTime")}
								</th>
							</tr>
						</thead>
						<tbody>
							{ROW_KEYS.map((rowKey) => (
								<tr key={rowKey} className="border-b border-[#e0e0e0]">
									<td className="w-[10%] py-4 text-sm font-bold text-black md:py-5 md:text-base">
										{t(`${rowKey}Class`)}
									</td>
									<td className="w-[35%] py-4 text-sm text-black md:py-5 md:text-base">
										{t(`${rowKey}Purpose`)}
									</td>
									<td className="py-4 text-sm text-black md:py-5 md:text-base lg:pl-[100px]">
										{t(`${rowKey}ShotLife`)}
									</td>
									<td className="py-4 text-sm text-black md:py-5 md:text-base">{t(`${rowKey}Tolerance`)}</td>
									<td className="py-4 text-sm text-black md:py-5 md:text-base">{t(`${rowKey}Cost`)}</td>
									<td className="py-4 text-sm text-black md:py-5 md:text-base">{t(`${rowKey}LeadTime`)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}
